import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SystemSettings } from './admin/SystemSettings';
import { MyProfileDialog } from './profile/MyProfileDialog';
import { Progress } from '@/components/ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger, SidebarFooter, SidebarHeader } from '@/components/ui/sidebar';
import { Home, Bell, Building2, FileText, BarChart3, Activity, LogOut, Settings, HelpCircle, MessageSquare, Plus, History, User as UserIcon, Users, UserCog, ClipboardCheck, Clock, StickyNote, ListChecks, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useAlerts } from '@/hooks/useAlerts';
import { useClients } from '@/hooks/useClients';
import { useSites } from '@/hooks/useSites';
import { useRooms } from '@/hooks/useRooms';
import { useClientPhotos } from '@/hooks/useClientPhotos';

import { useFeatureToggles } from '@/hooks/useFeatureToggles';

import UserGuide from './UserGuide';
import { AIHelpChat } from './AIHelpChat';
import AlertNotificationSettings from './admin/AlertNotificationSettings';

import OnboardingWizard from './admin/OnboardingWizard';
import { FacilityProfileView } from './manager/FacilityProfileView';
import { AlertsSection } from './manager/AlertsSection';
import { NotesSection } from './manager/NotesSection';
import { EnhancedServicesSection as ServicesSection } from './manager/EnhancedServicesSection';
import AnalyticsHub from './analytics/AnalyticsHub';
import { FacilitiesSection } from './manager/FacilitiesSection';
import { ClientManagement } from './manager/ClientManagement';
import { InspectionsModule } from '@/components/inspections/InspectionsModule';
import { OverdueTrainingsCard } from './manager/OverdueTrainingsCard';
import ClientProfile from './ClientProfile';
import { UserActivityLogs } from './admin/UserActivityLogs';
import { StaffSection } from './manager/StaffSection';
import { ClientsTab } from './clients/ClientsTab';
import ShiftReport from './staff/ShiftReport';
import { Users as UsersIcon, Bed } from 'lucide-react';
import { isClientOnPass, getStatusColor, resolveStatusKey, getStatusLabel } from '@/utils/statusTransitions';
import { useSidebarOrder, NavItem } from '@/hooks/useSidebarOrder';
import { DraggableSidebarNav } from '@/components/DraggableSidebarNav';
import { GlobalSearchCommand } from './GlobalSearchCommand';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import RosterManagement from './RosterManagement';
import { ComplianceModule } from './compliance/ComplianceModule';
import { ShieldCheck } from 'lucide-react';
import SuperAdminHome from './admin/SuperAdminHome';
const ManagerDashboard = () => {
  const {
    user,
    logout,
  } = useAuth();
  const [activeView, setActiveView] = useState('home');
  const [showMyProfile, setShowMyProfile] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [showAIHelp, setShowAIHelp] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<any>(null);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [rostersDialogOpen, setRostersDialogOpen] = useState(false);
  const [censusSort, setCensusSort] = useState<{ key: 'name' | 'facility' | 'room' | 'checkIn' | 'status'; dir: 'asc' | 'desc' } | null>(null);
  const toggleCensusSort = (key: 'name' | 'facility' | 'room' | 'checkIn' | 'status') => {
    setCensusSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };
  const compareRoom = (a: string, b: string) => {
    const re = /^(\d+)?(.*)$/;
    const [, an, as] = a.match(re) || [];
    const [, bn, bs] = b.match(re) || [];
    const ai = an ? parseInt(an, 10) : Number.POSITIVE_INFINITY;
    const bi = bn ? parseInt(bn, 10) : Number.POSITIVE_INFINITY;
    if (ai !== bi) return ai - bi;
    return (as || '').localeCompare(bs || '');
  };
  const {
    alerts: dbAlerts = []
  } = useAlerts();
  const {
    clients: allClients = [],
    updateClient
  } = useClients();
  const {
    sites: dbSites = [],
    isLoading: sitesLoading
  } = useSites();
  const { rooms = [] } = useRooms();
  const { getClientPhoto } = useClientPhotos(allClients);
  const {
    isFeatureEnabled
  } = useFeatureToggles();

  // Tab visibility based on feature toggles - check user's actual role
  const userRole = user?.role;
  const canSeeFacilities = isFeatureEnabled('manager_facilities_tab', userRole);
  const canSeeClients = isFeatureEnabled('manager_clients_tab', userRole);
  // Rosters tab retired — folded into Clients tab
  const canSeeInspections = isFeatureEnabled('manager_inspections_tab', userRole);
  const canSeeNotes = isFeatureEnabled('manager_notes_tab', userRole);
  const canSeeServices = isFeatureEnabled('manager_services_tab', userRole);
  const canSeeAnalytics = isFeatureEnabled('manager_analytics_tab', userRole);
  const canSeeAIAssistant = isFeatureEnabled('manager_ai_assistant_tab', userRole);
  const canSeeStaff = isFeatureEnabled('manager_staff_tab', userRole);
  const canSeeShiftReport = isFeatureEnabled('manager_shift_report_tab', userRole);
  const canSeeActivityLogs = isFeatureEnabled('manager_activity_logs_tab', userRole);
  const canSeeCompliance = isFeatureEnabled('compliance_module', userRole);

  const isDemo = false;
  const sites = dbSites;
  const alerts = dbAlerts;
  const notes: any[] = [];
  const activeAlerts = alerts.filter((alert: any) => alert.status === 'active');
  
  const effectiveClients = allClients;
  const checkedInCount = effectiveClients.filter((c: any) => c.status === 'checked-in' || c.status === 'checked_in').length;
  const totalCapacity = rooms.reduce((sum: number, r: any) => sum + (r.capacity || 1), 0);
  const beddedCount = useMemo(() => {
    return effectiveClients.filter((c: any) => c.room_id).length;
  }, [effectiveClients]);
  const occupancyRate = totalCapacity > 0 ? checkedInCount / totalCapacity * 100 : 0;

  const canAddFacility = userRole === 'manager' || userRole === 'program_director' || userRole === 'super_admin';

  // Define navigation items with visibility based on feature toggles
  // Note: Alerts removed from sidebar - accessible via header bell icon
  const defaultNavItems: NavItem[] = useMemo(() => [{
    id: 'home',
    label: 'Home',
    icon: Home,
    view: 'home',
    visible: true
  }, {
    id: 'facilities',
    label: 'Facilities',
    icon: Building2,
    view: 'facilities',
    visible: canSeeFacilities
  }, {
    id: 'clients',
    label: 'Clients',
    icon: UserCog,
    view: 'clients',
    visible: canSeeClients
  }, {
    id: 'inspections',
    label: 'Inspections',
    icon: ClipboardCheck,
    view: 'inspections',
    visible: canSeeInspections
  }, {
    id: 'services',
    label: 'Services',
    icon: Activity,
    view: 'services',
    visible: canSeeServices
  }, {
    id: 'notes',
    label: 'Notes',
    icon: StickyNote,
    view: 'notes',
    visible: canSeeNotes
  }, {
    id: 'staff',
    label: 'Staff',
    icon: Users,
    view: 'staff',
    visible: canSeeStaff
  }, {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    view: 'analytics',
    visible: canSeeAnalytics
  }, {
    id: 'ai-assistant',
    label: 'AI Assistant',
    icon: MessageSquare,
    view: 'ai-assistant',
    visible: canSeeAIAssistant
  }, {
    id: 'shift-report',
    label: 'Shift Report',
    icon: Clock,
    view: 'shift-report',
    visible: canSeeShiftReport
  }, {
    id: 'activity-logs',
    label: 'Activity Logs',
    icon: History,
    view: 'activity-logs',
    visible: canSeeActivityLogs
  }, {
    id: 'compliance',
    label: 'Compliance',
    icon: ShieldCheck,
    view: 'compliance',
    visible: canSeeCompliance
  }], [canSeeFacilities, canSeeClients, canSeeInspections, canSeeServices, canSeeNotes, canSeeStaff, canSeeAnalytics, canSeeAIAssistant, canSeeShiftReport, canSeeActivityLogs, canSeeCompliance]);
  const {
    items: navItems,
    reorderItems,
    resetOrder
  } = useSidebarOrder({
    storageKey: 'manager-sidebar-order',
    defaultItems: defaultNavItems
  });

  // Check if we should show onboarding (not for demo user)
  useEffect(() => {
    if (!sitesLoading && !hasCheckedOnboarding && !isDemo) {
      const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
      if (!hasSeenOnboarding && sites.length === 0) {
        setShowOnboarding(true);
      }
      setHasCheckedOnboarding(true);
    }
  }, [sitesLoading, sites.length, hasCheckedOnboarding, isDemo]);
  const handleOnboardingComplete = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    setShowOnboarding(false);
    setActiveView('home');
  };
  const handleOnboardingSkip = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    setShowOnboarding(false);
  };
  const handleLogout = () => {
    logout();
  };
  const openClientProfile = (client: any) => {
    const displayName = typeof client?.name === 'string' && client.name.trim()
      ? client.name.trim()
      : `${client?.first_name || ''} ${client?.last_name || ''}`.trim();
    setSelectedClient({
      id: client.id,
      name: displayName || 'Unknown',
      room: client.room || client.rooms?.room_number || (client.room_id ? String(client.room_id) : 'Unassigned'),
      status: client.status || 'unbedded',
      caseWorker: client.caseWorker || client.case_worker_id || 'Case Manager',
      phone: client.phone || '',
      email: client.email || '',
      alerts: [],
      checkInTime: client.checkInTime || client.check_in_time,
      checkOutTime: client.checkOutTime || client.check_out_time,
      site_id: client.site_id,
      photo: client.photo || client.photo_url,
    });
  };
  const renderContent = () => {
    // Show facility profile if a facility is selected
    if (selectedFacility) {
      return <FacilityProfileView facility={selectedFacility} onBack={() => setSelectedFacility(null)} onSelectClient={clientId => {
        const client = allClients.find((c: any) => c.id === clientId);
        if (client) {
          const displayName = typeof (client as any).name === 'string' && (client as any).name.trim() ? (client as any).name.trim() : `${(client as any).first_name || (client as any).firstName || ''} ${(client as any).last_name || (client as any).lastName || ''}`.trim();
          setSelectedClient({
            id: client.id,
            name: displayName || 'Unknown',
            room: (client as any).room || (client as any).roomNumber || 'Unassigned',
            status: client.status || 'unbedded',
            caseWorker: (client as any).caseWorker || 'Case Manager',
            phone: (client as any).phone || '',
            email: (client as any).email || '',
            alerts: [],
            checkInTime: (client as any).checkInTime,
            checkOutTime: (client as any).checkOutTime,
            site_id: (client as any).site_id || (client as any).siteId,
            photo: (client as any).photo || (client as any).photo_url
          });
        }
      }} isDemo={isDemo} />;
    }
    switch (activeView) {
      case 'home':
        if (userRole === 'program_director') {
          return (
            <SuperAdminHome
              clients={effectiveClients}
              rooms={rooms}
              sites={sites}
              alerts={alerts}
              totalCapacity={totalCapacity}
              checkedInCount={checkedInCount}
              dailyRosterCount={checkedInCount}
              userName={user?.name || 'Program Director'}
              showPerformanceCard={false}
            />
          );
        }
        return <div className="space-y-4">
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <h2 className="text-lg sm:text-2xl font-bold truncate">Welcome back, {user?.name || 'Manager'}</h2>
                <p className="text-xs sm:text-sm text-muted-foreground">Here's an overview of your shelter operations.</p>
              </div>
              {canAddFacility}
            </div>

            {/* Quick Stats — Status Breakdown */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
              {[
                { key: 'checkedIn', label: 'Checked In', clients: effectiveClients.filter((c: any) => c.status === 'checked-in' || c.status === 'checked_in'), colorClass: 'text-[hsl(var(--status-checked-in))]' },
                { key: 'checkedOut', label: 'Checked Out', clients: effectiveClients.filter((c: any) => c.status === 'checked-out' || c.status === 'checked_out'), colorClass: 'text-[hsl(var(--status-checked-out))]' },
                { key: 'unbedded', label: 'Unbedded', clients: effectiveClients.filter((c: any) => c.status === 'unbedded'), colorClass: 'text-[hsl(var(--status-unbedded))]' },
                { key: 'pass', label: 'Pass', clients: effectiveClients.filter((c: any) => isClientOnPass(c)), colorClass: 'text-[hsl(var(--status-pass))]' },
                { key: 'notPresent', label: 'Not Present', clients: effectiveClients.filter((c: any) => c.status === 'not_present' || c.status === 'not-present'), colorClass: 'text-[hsl(var(--status-not-present))]' },
                { key: 'waitlist', label: 'Waitlist', clients: effectiveClients.filter((c: any) => c.status === 'waitlist'), colorClass: 'text-[hsl(var(--status-waitlist))]' },
              ].map((stat) => (
                <Popover key={stat.key}>
                  <PopoverTrigger asChild>
                    <Card className="cursor-pointer hover:ring-2 hover:ring-primary/30 transition-all">
                      <CardContent className="p-3 sm:p-4">
                        <div className="text-center">
                          <p className={`text-2xl sm:text-3xl font-bold tabular-nums ${stat.colorClass}`}>{stat.clients.length}</p>
                          <p className="text-[11px] sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 uppercase sm:normal-case tracking-wide sm:tracking-normal leading-tight">{stat.label}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 max-h-72 overflow-y-auto">
                    <p className="font-semibold mb-2">{stat.label} ({stat.clients.length})</p>
                    {stat.clients.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No clients</p>
                    ) : (
                      stat.clients.map((c: any) => {
                        const name = c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown';
                        return (
                          <button
                            key={c.id}
                            onClick={() => openClientProfile(c)}
                            className="block w-full text-left hover:underline p-1 text-sm"
                          >
                            {name}
                          </button>
                        );
                      })
                    )}
                  </PopoverContent>
                </Popover>
              ))}
            </div>

            {/* Daily Census - default main view, full width */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Daily Census</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setRostersDialogOpen(true)} className="gap-2">
                    <ListChecks className="h-4 w-4" />
                    Manage Rosters
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {effectiveClients.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <UsersIcon className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Clients</h3>
                    <p className="text-sm text-muted-foreground">No clients are currently in the system</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          {([
                            { key: 'name', label: 'Name', width: 'w-[30%]' },
                            { key: 'facility', label: 'Facility', width: 'w-[15%]' },
                            { key: 'room', label: 'Room', width: 'w-[12%]' },
                            { key: 'checkIn', label: 'Check-In', width: 'w-[18%]' },
                            { key: 'status', label: 'Status', width: 'w-[25%]' },
                          ] as const).map((col) => {
                            const active = censusSort?.key === col.key;
                            const Icon = active ? (censusSort!.dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
                            return (
                              <th
                                key={col.key}
                                className={`text-left p-4 font-medium text-muted-foreground ${col.width} cursor-pointer select-none`}
                                onClick={() => toggleCensusSort(col.key)}
                              >
                                <span className="inline-flex items-center gap-1.5">
                                  {col.label}
                                  <Icon className={`h-3.5 w-3.5 ${active ? 'opacity-100' : 'opacity-40'}`} />
                                </span>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {[...effectiveClients]
                          .sort((a: any, b: any) => {
                            if (censusSort) {
                              const dirMul = censusSort.dir === 'asc' ? 1 : -1;
                              const getVal = (c: any): { empty: boolean; value: any } => {
                                const room = rooms.find((r: any) => r.id === c.room_id);
                                const site = sites.find((s: any) => s.id === c.site_id);
                                const name = (c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || '').toLowerCase();
                                const checkInTime = c.checkInTime ?? c.check_in_time ?? c.fallback_checkin_time;
                                switch (censusSort.key) {
                                  case 'name': return { empty: !name, value: name };
                                  case 'facility': return { empty: !site?.name, value: (site?.name || '').toLowerCase() };
                                  case 'room': return { empty: !room?.room_number, value: room?.room_number || '' };
                                  case 'checkIn': return { empty: !checkInTime, value: checkInTime ? new Date(checkInTime).getTime() : 0 };
                                  case 'status': return { empty: !c.status, value: getStatusLabel(resolveStatusKey(c)).toLowerCase() };
                                }
                              };
                              const av = getVal(a);
                              const bv = getVal(b);
                              if (av.empty && !bv.empty) return 1;
                              if (!av.empty && bv.empty) return -1;
                              if (av.empty && bv.empty) return 0;
                              if (censusSort.key === 'room') return compareRoom(av.value, bv.value) * dirMul;
                              if (av.value < bv.value) return -1 * dirMul;
                              if (av.value > bv.value) return 1 * dirMul;
                              return 0;
                            }
                            const aIsUnbedded = (a.status || 'unbedded') === 'unbedded';
                            const bIsUnbedded = (b.status || 'unbedded') === 'unbedded';
                            if (aIsUnbedded && !bIsUnbedded) return 1;
                            if (!aIsUnbedded && bIsUnbedded) return -1;
                            const aTime = a.checkInTime ?? a.check_in_time ?? a.fallback_checkin_time;
                            const bTime = b.checkInTime ?? b.check_in_time ?? b.fallback_checkin_time;
                            const aT = aTime ? new Date(aTime).getTime() : 0;
                            const bT = bTime ? new Date(bTime).getTime() : 0;
                            return bT - aT;
                          })
                          .map((c: any) => {
                            const room = rooms.find((r: any) => r.id === c.room_id);
                            const site = sites.find((s: any) => s.id === c.site_id);
                            const name = c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown';
                            const status = c.status || 'unbedded';
                            const checkInTime = c.checkInTime ?? c.check_in_time ?? c.fallback_checkin_time;
                            const statusKey = resolveStatusKey(c);
                            return (
                              <tr key={c.id} className="border-b hover:bg-muted/30">
                                <td className="p-4 font-medium">
                                  <button
                                    type="button"
                                    onClick={() => openClientProfile(c)}
                                    className="text-primary hover:underline underline-offset-2 text-left"
                                  >
                                    {name}
                                  </button>
                                </td>
                                <td className="p-4 text-sm text-muted-foreground">{site?.name || '—'}</td>
                                <td className="p-4 text-sm">{room?.room_number || '—'}</td>
                                <td className="p-4 text-sm text-muted-foreground">
                                  {checkInTime
                                    ? new Date(checkInTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                                    : status === 'checked-in' ? 'Today' : '—'}
                                </td>
                                <td className="p-4">
                                  <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${getStatusColor(statusKey, 'soft')}`}>
                                    {getStatusLabel(statusKey)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Overdue Trainings */}
            <OverdueTrainingsCard onViewAll={() => setActiveView('staff')} />

            {sites.length === 0 && <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <Building2 className="h-10 w-10 text-muted-foreground mb-3" />
                  <h3 className="text-base font-semibold mb-1">No Facilities Yet</h3>
                  <p className="text-sm text-muted-foreground text-center mb-3">
                    Set up your first facility to begin managing your shelter
                  </p>
                  <Button onClick={() => setActiveView('facilities')} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Your First Facility
                  </Button>
                </CardContent>
              </Card>}
          </div>;
      case 'alerts':
        return <AlertsSection isDemo={isDemo} />;
      case 'facilities':
        return <FacilitiesSection isDemo={isDemo} canAddFacility={canAddFacility} />;
      case 'clients':
        return <ClientsTab onSelectClient={client => {
          const displayName = typeof (client as any)?.name === 'string' && (client as any).name.trim() ? (client as any).name.trim() : `${(client as any)?.first_name || ''} ${(client as any)?.last_name || ''}`.trim();
          const transformedClient = {
            id: (client as any).id,
            name: displayName || 'Unknown',
            room: (client as any).room || (client as any).rooms?.room_number as string | undefined || ((client as any).room_id ? String((client as any).room_id) : 'Unassigned'),
            status: (client as any).status || 'unbedded',
            caseWorker: (client as any).caseWorker || (client as any).case_worker_id || 'Case Manager',
            phone: (client as any).phone || '',
            email: (client as any).email || '',
            alerts: [],
            checkInTime: (client as any).checkInTime || (client as any).check_in_time,
            checkOutTime: (client as any).checkOutTime || (client as any).check_out_time,
            site_id: (client as any).site_id,
            photo: (client as any).photo || (client as any).photo_url
          };
          setSelectedClient(transformedClient);
        }} />;
      case 'inspections':
        return <InspectionsModule />;
      case 'rosters':
        // Rosters tab retired — folded into Clients tab (⋯ → Manage Rosters)
        return <ClientsTab />;
      case 'notes':
        return <NotesSection isDemo={isDemo} />;
      case 'analytics':
        return <AnalyticsHub
          isDemo={isDemo}
          clients={effectiveClients}
          alerts={alerts}
          totalCapacity={totalCapacity}
          checkedInCount={beddedCount}
          dailyRosterCount={beddedCount}
        />;
      case 'ai-assistant':
        return <div className="space-y-4 h-[calc(100vh-8rem)]">
            <div>
              <h2 className="text-xl font-semibold">AI Dashboard Assistant</h2>
              <p className="text-sm text-muted-foreground">Ask questions about your facilities</p>
            </div>
            <div className="h-[calc(100%-4rem)]">
              <AIHelpChat />
            </div>
          </div>;
      case 'notifications':
        return <AlertNotificationSettings />;
      case 'settings':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold">Settings</h2>
              <p className="text-sm text-muted-foreground">Manage your preferences and security</p>
            </div>
            <Tabs defaultValue="notifications" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="notifications">Notifications</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
              </TabsList>
              <TabsContent value="notifications" className="mt-6">
                <AlertNotificationSettings />
              </TabsContent>
              <TabsContent value="security" className="mt-6">
                <SystemSettings settingsFilter="security" />
              </TabsContent>
            </Tabs>
          </div>
        );
      case 'services':
        return <ServicesSection />;
      case 'activity-logs':
        return <UserActivityLogs isDemo={isDemo} />;
      case 'staff':
        return <StaffSection isDemo={isDemo} />;
      case 'shift-report':
        return <ShiftReport onBack={() => setActiveView('home')} />;
      case 'compliance':
        return <ComplianceModule />;
      default:
        return null;
    }
  };

  // Show onboarding wizard for new managers
  if (showOnboarding) {
    return <OnboardingWizard onComplete={handleOnboardingComplete} onSkip={handleOnboardingSkip} />;
  }
  return <SidebarProvider defaultOpen>
      <div className="flex min-h-screen w-full">
        <Sidebar collapsible="icon">
          <SidebarHeader className="border-b px-4 h-14 flex items-center">
            <span className="group-data-[collapsible=icon]:hidden text-lg font-semibold">Navigation</span>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <DraggableSidebarNav items={navItems} activeView={activeView} onViewChange={setActiveView} onReorder={reorderItems} />
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter className="border-t p-2">
            <SidebarTrigger className="w-full justify-center" />
          </SidebarFooter>
        </Sidebar>

        <div className="flex-1 flex flex-col min-w-0">
          <header className="border-b sticky top-0 z-10 bg-background">
            <div className="flex h-14 items-center px-4 lg:px-6">
              <SidebarTrigger className="lg:hidden" />
              <GlobalSearchCommand />
              <div className="flex-1" />
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => setActiveView('alerts')} className="relative" title="Alerts">
                  <Bell className="h-5 w-5" />
                  {activeAlerts.length > 0 && <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {activeAlerts.length}
                    </span>}
                </Button>
                {canSeeAIAssistant && <Button variant="ghost" size="icon" onClick={() => setShowAIHelp(true)} title="AI Help">
                    <HelpCircle className="h-5 w-5" />
                  </Button>}
                
                {/* User Avatar & Profile Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 px-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <UserIcon className="h-4 w-4 text-primary" />
                      </div>
                      <span className="hidden md:inline-block text-sm font-medium max-w-[120px] truncate">
                        {user?.name || 'Manager'}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-popover border shadow-md">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">{user?.name || 'User'}</p>
                        <p className="text-xs text-muted-foreground capitalize">{user?.role || 'Manager'}</p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setShowMyProfile(true)}>
                      <UserIcon className="mr-2 h-4 w-4" />
                      My Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveView('settings')}>
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowUserGuide(true)}>
                      <HelpCircle className="mr-2 h-4 w-4" />
                      User Guide
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto p-4 lg:p-6">
            {renderContent()}
          </main>
        </div>
      </div>

      {showUserGuide && <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-auto">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>User Guide</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowUserGuide(false)}>
                  Close
                </Button>
              </CardHeader>
              <CardContent>
                <UserGuide compact />
              </CardContent>
            </Card>
          </div>
        </div>}

      {canSeeAIAssistant && showAIHelp && <div className="fixed bottom-4 right-4 w-80 h-[500px] z-50">
          <Card className="h-full flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                AI Help
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAIHelp(false)}>
                Close
              </Button>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
              <AIHelpChat />
            </CardContent>
          </Card>
        </div>}

      {/* Client Profile Dialog */}
      <ClientProfile
        client={selectedClient}
        isOpen={!!selectedClient}
        onClose={() => setSelectedClient(null)}
        getClientPhoto={getClientPhoto}
        onUpdateClient={(updated) => {
          updateClient(updated);
          setSelectedClient(updated);
        }}
      />
      
      {/* My Profile Dialog */}
      <MyProfileDialog open={showMyProfile} onOpenChange={setShowMyProfile} />

      {/* Manage Rosters Dialog */}
      <Dialog open={rostersDialogOpen} onOpenChange={setRostersDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Rosters</DialogTitle>
          </DialogHeader>
          <RosterManagement />
        </DialogContent>
      </Dialog>
    </SidebarProvider>;
};
export default ManagerDashboard;